import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import {
  Box,
  Heading,
  Stack,
  Text,
  TextLink,
  useResponsiveValue
} from '@machobear/design-system'

import emailIcon from '@/features/Company/icons/email-icon.png'
import globeIcon from '@/features/Company/icons/globe-icon.png'
import markerIcon from '@/features/Company/icons/marker-icon.png'
import phoneIcon from '@/features/Company/icons/phone-icon.png'
import Head from '@/components/head'

import heroBanner from './vantage-west-realty-hero.webp'
import titleLogo from './vantage-west-realty-logo.png'
import qrCode from './vantage-west-realty-qr.jpg'

const VantageWestRealtyProfile = ({
  metadata,
  footerOnScreen,
  listingsOnScreen
}) => {
  const { t } = useTranslation('vantagewestrealty')
  const profile = {
    banner: heroBanner.src,
    title: t('vantagewestrealty:Vantage West Realty Inc.'),
    pageDescription: t(
      'vantagewestrealty:description1',
      'Operating out of Kelowna, British Columbia, we provide our property management services to the Okanagan Valley, including Kelowna, West Kelowna, Peachland, Lake Country, Vernon, Penticton and neighbouring communities.'
    ),
    url: '/company/vantagewestrealty'
  }

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })

  const isDesktop = useResponsiveValue()({
    mobile: false,
    tablet: false,
    desktop: true
  })

  return (
    <>
      <Head
        title={`${profile.title} | liv.rent`}
        description={profile.pageDescription}
        path={profile.url}
        pageData={metadata}
        hideCustomerSupport
      />
      <Box
        style={{ height: isMobile ? '210px' : isDesktop ? '575px' : '350px' }}
        marginBottom="gutter"
        overflow="hidden"
        position="relative"
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="full"
          width="full"
          overflow="hidden"
        >
          <Image
            fill
            priority
            src={heroBanner.src}
            style={{
              objectFit: 'cover',
              objectPosition: '50% 50%'
            }}
            sizes="(max-width: 768px) 768px, (max-width: 1200px) 1200px"
            alt=""
          />
        </Box>
      </Box>
      <Box
        display="flex"
        justifyContent="center"
        width="full"
        marginBottom="large"
      >
        <Stack space="medium">
          <Box display="flex" alignItems="center" marginRight="large">
            <Box
              marginRight="xsmall"
              marginLeft="medium"
              style={{ flexShrink: 0 }}
            >
              <Image
                src={titleLogo.src}
                width={150}
                height={40}
                alt="Vantage West Realty"
              />
            </Box>
            <Heading level="3" weight="weak">
              {t('vantagewestrealty:Vantage West Realty Inc.')}
            </Heading>
          </Box>
          <Box
            display="flex"
            flexDirection={{ mobile: 'column', tablet: 'row' }}
            gap={{ mobile: 'gutter', tablet: 'none' }}
            marginX="medium"
            marginBottom="xlarge"
            marginTop="medium"
            style={{
              maxWidth: '1096px'
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              marginRight="xxlarge"
              style={{
                width: isMobile ? '100%' : 'fit-content'
              }}
            >
              <Stack space="medium">
                <Stack space="gutter">
                  <Text size="standard">
                    {t(
                      'vantagewestrealty:description1',
                      'Operating out of Kelowna, British Columbia, we provide our property management services to the Okanagan Valley, including Kelowna, West Kelowna, Peachland, Lake Country, Vernon, Penticton and neighbouring communities.'
                    )}
                  </Text>
                  <Text size="standard">
                    {t(
                      'vantagewestrealty:description2',
                      'As one of the leading property management companies in the area, our team of property managers understands how to manage your investment property. Our services bring a fully customized solution to help you as a landlord fill your vacant property, find excellent tenants, and make more on your properties.'
                    )}
                  </Text>
                  <Text size="standard">
                    {t(
                      'vantagewestrealty:description3',
                      'Our professional property managers work hard so that owners don’t need to deal with the daily hassles and stress of managing rental properties, and give each of our clients our full attention to ensure they have a great experience with our company.'
                    )}
                  </Text>
                  <Text size="standard">
                    {t(
                      'vantagewestrealty:description4',
                      'When you work with us, you’re in good hands.'
                    )}
                  </Text>
                </Stack>
              </Stack>
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              background="neutralSoft"
              borderRadius="small"
              paddingY="small"
              paddingX="gutter"
              marginTop={{ mobile: 'gutter', tablet: 'none' }}
              style={{
                height: 'min-content',
                width: isMobile ? '100%' : 'fit-content'
              }}
            >
              <Stack space="medium">
                <Box display="flex" alignItems="center" width="full">
                  <Box
                    marginRight="xxsmall"
                    style={{ width: '16px', flexShrink: 0 }}
                  >
                    <Image src={globeIcon.src} alt="" width={16} height={16} />
                  </Box>
                  <TextLink
                    href="https://www.propertymanagementkelowna.com/"
                    rel="noreferrer noopener"
                    target="_blank"
                  >
                    www.propertymanagementkelowna.com
                  </TextLink>
                </Box>
                <Box display="flex" alignItems="center" width="full">
                  <Box
                    marginRight="xxsmall"
                    style={{ width: '16px', flexShrink: 0 }}
                  >
                    <Image src={phoneIcon.src} alt="" width={16} height={16} />
                  </Box>
                  <TextLink href="tel:604 688 9460" rel="noreferrer noopener">
                    604.688.9460
                  </TextLink>
                </Box>
                <Box display="flex" alignItems="center" width="full">
                  <Box
                    marginRight="xxsmall"
                    style={{ width: '16px', flexShrink: 0 }}
                  >
                    <Image src={emailIcon.src} alt="" width={16} height={16} />
                  </Box>
                  <TextLink
                    href="mailto:ecapplication@vantagewestrealty.com"
                    rel="noreferrer noopener"
                  >
                    ecapplication@vantagewestrealty.com
                  </TextLink>
                </Box>
                <Box display="flex" alignItems="center" width="full">
                  <Box
                    marginRight="xxsmall"
                    style={{ width: '16px', flexShrink: 0 }}
                  >
                    <Image src={markerIcon.src} alt="" width={16} height={16} />
                  </Box>
                  <TextLink
                    href="https://maps.app.goo.gl/1ygJhoNxBN9m8noj7"
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    #120-1060 Manhattan Dr, Kelowna BC V1Y 9X9
                  </TextLink>
                </Box>
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Box>
      <Box textAlign="center" paddingBottom="gutter">
        <Heading level="6">{t('vantagewestrealty:Our Portfolio')}</Heading>
      </Box>
      {isDesktop && (
        <Box
          position={footerOnScreen && listingsOnScreen ? 'absolute' : 'fixed'}
          zIndex="dropdown"
          border="borderNeutralLight"
          background="bodyWhite"
          display={{ mobile: 'none', tablet: 'none', desktop: 'flex' }}
          style={{
            top: !footerOnScreen && !listingsOnScreen ? '1046px' : null,
            bottom: '20px',
            right: '10px'
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            position="relative"
            justifyContent="center"
            style={{ width: '158px', height: '175px' }}
          >
            <Image
              src={qrCode}
              alt="QR Code for Available Properties"
              width={160}
              height={160}
              placeholder="blur"
            />
            <Text align="center" size="xsmall" weight="medium">
              {t('vantagewestrealty:Available Properties')}
            </Text>
          </Box>
        </Box>
      )}
    </>
  )
}

export default VantageWestRealtyProfile
